import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import MultiSelect from '../../components/MultiSelect';

const PlacesToVisit = () => {
  const { locationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('listing');
  const [allPackages, setAllPackages] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [metaData, setMetaData] = useState({
    metaTitle: '',
    metaKeyword: '',
    metaDescription: ''
  });
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);
  const dropdownRef = useRef(null);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`getPlacesToVisit/${locationId}`);
      
      if (response.status) {
        setLocations(response.data.locations || []);
        setLocationName(response.data.locationName || '');
        setSelectedPlaces(response.data.data?.placestovisit?.map(p => ({ ...p, shortDescription: p.shortDescription || '' })) || []);
        setMetaData({
          metaTitle: response.data.data?.metaTitle || '',
          metaKeyword: response.data.data?.metaKeyword || '',
          metaDescription: response.data.data?.metaDescription || ''
        });
        const kw = response.data.data?.keywordLink && response.data.data.keywordLink.length > 0
          ? response.data.data.keywordLink.map(k => ({ keyword: k.keyword || '', link: k.link || '' }))
          : [{ keyword: '', link: '' }];
        setKeywordLinks(kw);

        const selPkgs = (response.data.data?.packages || []).map(p => typeof p === 'object' ? p._id : p);
        setSelectedPackages(selPkgs);

        const allPkgs = (response.data.activePackages || []).map(p => ({
          ...p,
          name: p.vendorId?.business_name ? `${p.name} (${p.vendorId.business_name})` : p.name
        }));
        setAllPackages(allPkgs);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const filteredKeywordLinks = keywordLinks.filter(k => k.keyword?.trim() || k.link?.trim());
      const deleteAllKeywordLink = filteredKeywordLinks.length === 0 ? '1' : '0';

      const response = await apiService.post('updatePlacesToVisit', {
        locationId,
        placestovisit: selectedPlaces.map(p => ({ _id: p._id, shortDescription: p.shortDescription || '' })),
        metaTitle: metaData.metaTitle,
        metaKeyword: metaData.metaKeyword,
        metaDescription: metaData.metaDescription,
        keywordLink: filteredKeywordLinks,
        delete_all_keywordLink: deleteAllKeywordLink,
        packages: selectedPackages
      });
      
      if (response.status) {
        toast.success('Places to visit updated successfully');
        await fetchData();
      } else {
        toast.error(response.message || 'Failed to save');
      }
    } catch (error) {
      toast.error('Something went wrong!');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const addPlace = (location) => {
    if (!selectedPlaces.find(p => p._id === location._id)) {
      setSelectedPlaces([...selectedPlaces, { ...location, shortDescription: location.shortDescription || '' }]);
    }
    setShowDropdown(false);
    setSearchTerm('');
  };

  const updateShortDescription = (placeId, value) => {
    setSelectedPlaces(prev => prev.map(p => p._id === placeId ? { ...p, shortDescription: value } : p));
  };

  const removePlace = (placeId) => {
    setSelectedPlaces(selectedPlaces.filter(p => p._id !== placeId));
  };

  const filteredLocations = locations.filter(loc => 
    loc._id !== locationId && 
    !selectedPlaces.find(p => p._id === loc._id) &&
    loc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7]"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  const tabs = [
    { id: 'listing', label: 'Listing', icon: 'bi-list-task' },
    { id: 'seo', label: 'SEO Points', icon: 'bi-search' },
    { id: 'keywordLink', label: 'Keyword Links', icon: 'bi-link-45deg' },
    { id: 'packages', label: 'Packages', icon: 'bi-box-seam' }
  ];

  return (
    <div className="p-0">
      <div className="mb-6">
        <nav className="text-sm flex items-center mb-2">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-location" className="text-gray-500 hover:text-gray-700">Locations</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Places To Visit</span>
        </nav>
        {locationName && (
          <h1 className="text-xl font-bold text-gray-900">{locationName}</h1>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto bg-gray-50/50 rounded-t-lg">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? 'border-[#009ef7] text-[#009ef7] bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`bi ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'listing' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Add Location</label>
                  <span className="text-xs text-gray-500">{selectedPlaces.length} selected</span>
                </div>
                <div className="mb-4" ref={dropdownRef}>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-left flex items-center justify-between bg-white hover:border-gray-300 transition-colors shadow-sm outline-none"
                    >
                      <span className="text-sm text-gray-500">Select location...</span>
                      <i className={`bi bi-chevron-down text-sm transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
                    </button>
                    {showDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                        <div className="p-2 border-b">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search..."
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                          />
                        </div>
                        <div className="overflow-y-auto max-h-48">
                          {filteredLocations.map(location => (
                            <button
                              key={location._id}
                              type="button"
                              onClick={() => addPlace(location)}
                              className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors"
                            >
                              {location.name}
                            </button>
                          ))}
                          {filteredLocations.length === 0 && (
                            <div className="px-4 py-2.5 text-sm text-gray-500 text-center">No locations found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedPlaces.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedPlaces.map(place => (
                      <div key={place._id} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors bg-white">
                        <div className="flex items-center gap-3">
                          {place.image ? (
                            <img
                              src={`${mediaUrl}${place.image}`}
                              alt={place.name}
                              className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                              <i className="bi bi-image text-lg sm:text-xl text-gray-400"></i>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">{place.name}</h4>
                            {place.stateId && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">
                                <i className="bi bi-geo-alt mr-1"></i>
                                {place.stateId.name}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removePlace(place._id)}
                            className="px-2 sm:px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 font-medium"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                        <textarea
                          rows={2}
                          value={place.shortDescription || ''}
                          onChange={(e) => updateShortDescription(place._id, e.target.value)}
                          placeholder="Short description..."
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <i className="bi bi-geo-alt text-4xl mb-2 block"></i>
                    <p className="text-sm">No places selected yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Title</label>
                  <input
                    type="text"
                    value={metaData.metaTitle}
                    onChange={(e) => setMetaData((prev) => ({ ...prev, metaTitle: e.target.value }))}
                    placeholder="Enter meta title"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Keyword</label>
                  <textarea
                    value={metaData.metaKeyword}
                    onChange={(e) => setMetaData((prev) => ({ ...prev, metaKeyword: e.target.value }))}
                    placeholder="keyword1, keyword2, keyword3"
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Description</label>
                  <textarea
                    value={metaData.metaDescription}
                    onChange={(e) => setMetaData((prev) => ({ ...prev, metaDescription: e.target.value }))}
                    placeholder="Enter meta description"
                    rows="4"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                  />
                </div>
              </div>
            )}

            {activeTab === 'keywordLink' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Manage Keyword Links</h4>
                  <button
                    type="button"
                    onClick={addKeywordLink}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
                  >
                    <i className="bi bi-plus-circle"></i> Add Row
                  </button>
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keyword</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target Link / URL</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-16">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {keywordLinks.length > 0 ? (
                        keywordLinks.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={item.keyword}
                                onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)}
                                placeholder="e.g. Travel Guide"
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={item.link}
                                onChange={(e) => updateKeywordLink(index, 'link', e.target.value)}
                                placeholder="e.g. https://example.com/guide"
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                              />
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => removeKeywordLink(index)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-4 py-6 text-center text-gray-400 text-sm">
                            No keyword links added yet. Click "Add Row" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'packages' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Packages for Places To Visit</label>
                  <MultiSelect
                    options={allPackages}
                    value={selectedPackages}
                    onChange={(value) => setSelectedPackages(value)}
                    placeholder="Select packages"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Link
            to="/view-location"
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 no-underline text-sm font-medium transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#007acc] disabled:opacity-50 text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlacesToVisit;
